type UserDocument = {
  description: string
  document_id: number
  expiration_date?: string
  has_expiration: boolean
  id: number
  image?: string
  is_mandatory: boolean
  key1: string
  key2?: string
  license_number?: string
  license_type?: number
  name: string
  on_boarding_doc: boolean
  sort: number
  submitted: boolean
  title: string
}

type UserContact = {
  address?: string
  birth_date?: string
  city?: number
  country_id?: number
  first_name?: string
  last_name?: string
  phone?: string
}

type UserOnboardingData = UserContact & UserDocument

export type ApiResponse<T> = {
  data: T
}

export type AuthVerifyApiBody = {
  email: string
  provider: 'external' | 'normal'
}

export type OnboardingContactBody = {
  data: {
    address: string
    birth_date: string
    city_id: number
    first_name: string
    last_name: string
    phone: string
  }
  name: 'contact'
}

export type OnboardingProfilePictureBody = {
  data: {
    image: string
  }
  name: 'profile_picture'
}

export type OnboardingDriverLicenseBody = {
  data: {
    expiration_date: string
    key1: string
    key2: string
    license_number: string
    license_type: 51
  }
  document_id: number
}

export type OnboardingDocumentBody = {
  data: {
    expiration_date: string
    key1: string
  }
  document_id: number
}

export type OnboardingStepBody =
  | OnboardingContactBody
  | OnboardingDocumentBody
  | OnboardingDriverLicenseBody
  | OnboardingProfilePictureBody

export type OnboardingSkipBody = {
  document_id?: string
  name?: string
}

export type ApiPostResponse = {
  message: string
  success: boolean
}

export type AuthVerifyApiResponse = {
  data: UserOnboardingData[]
  message: string
  onBoarded: boolean
  sort: number
  success: boolean
  userId: number
}

export type PaginatedData<T> = {
  current_page: number
  items: T
  limit: number
  pages: number
  total_items: number
}

export type LicenceData = {
  id: number
  name: string
}

export type CityData = {
  city_id: number
  licenses: LicenceData[]
  min_age: number
  name: string
  province_id: number
  status: string
  tax_rate?: string
  vehicle_age: number
}

type LicenseType = {
  id: number
  name: string
}

type City = {
  id: number
  name: string
  province_id: number
  timezone: string
}

type Model = {
  make_id: number
  model_id: number
  name: string
}

type Make = {
  make_id: number
  name: string
}

export type Vehicle = {
  color: string
  make: Make
  model: Model
  plate_number: string
  registration_number?: string
  status?: string
  vehicle_id: number
  vin?: string
  year: number
}

export type SelectedVehicle = {
  expiration_date?: null | string
  full_name: string
  profile_picture: string
  vehicle: Vehicle
}

type AgreementForm = {
  current: boolean
  document: UserDocument
  document_id: number
  id: number
  key1: string
  status: string
}

export type DriverData = {
  address: string
  agreement_form: AgreementForm
  birth_date: string
  city: City
  created_at: string
  email: string
  first_name: string
  last_name: string
  license_number: string
  license_type: LicenseType
  phone: string
  pink_slip: boolean
  profile_picture: string
  status: string
  updated_at: string
  user_id: number
  vehicles: Vehicle[]
}

export type QueryParams = {
  filter?: Record<string, string>
  limit?: number
  page?: number
  sort?: Record<string, string>
}

export type CitiesRequestFilters = {
  country_id?: number
} & QueryParams

export type CountryData = {
  created_at: string
  id: number
  language: string
  name: string
  status: string
  updated_at: string
}

export type ProvinceData = {
  country: string
  created_at: string
  id: number
  name: string
  updated_at: string
}

export enum COUNTRIES {
  CANADA = 1,
  GERMANY = 3,
  MEXICO = 2,
}

export type RequestOptions = {
  noAuth?: boolean
}

interface TrainingItem {
  id: number
  title: string
  type: 'guide' | 'video'
  url: string
  watched: boolean
}

export interface Trainings {
  guides: TrainingItem[]
  videos: TrainingItem[]
}

interface Application {
  comment: string
  created_at: string
  guarantee_met: boolean
  id: number
  shift_id: number
  status: string
  updated_at: string
  user_id: number
}

export interface Shift {
  application?: Application
  city_id: number
  created_at: string
  duration: number
  end_time: string
  exists: boolean
  guarantee?: number
  guarantee_specs?: string
  id: number
  parent_id: number
  start_time: string
  status: string
  title: string
  updated_at: string
}

export interface ShiftPayload {
  accepted: Shift[]
  available: Shift[]
  pending: Shift[]
}

export type DocumentItem = {
  created_at: string
  days: number
  document: UserDocument
  editable: boolean
  expiration_date: string
  id: number
  key1: string
  key2: string
  license_number: string
  license_type: string
  notes: null | string
  status: string
  updated_at: string
}

export type ExtraDocument = {
  is_submitted: boolean
  key: string
  text: string
  value: null | string
}

export type DocumentsByUserData = {
  additional_documents: DocumentItem[]
  extra_documents: ExtraDocument[]
  user_documents: DocumentItem[]
  vehicle_documents: DocumentItem[]
}

export interface Notification {
  created_at: string
  id: number
  is_read: boolean
  message: string
  message_id: number
  read_at: string
  sent_at: string
  title: string
  updated_at: string
}

export interface Meta {
  is_paginated: boolean
  limit: number
  page: number
}

export interface NotificationsData {
  items: Notification[]
  meta: Meta
  total_items: number
}

interface Photo {
  created_at: string
  id: number
  key: string
  status: string
  type_id: number
  updated_at: string
  vehicle_id: number
}

interface VehiclesPhotosByDriverItem {
  created_at: string
  key: string
  photo: Photo
  status: string
  title: string
  type_id: number
  updated_at: string
}

export interface VehiclesPhotosByDriverData {
  items: VehiclesPhotosByDriverItem[]
  total_items: number
}

interface AdditionalDocument {
  created_at: string
  custom_name: string
  document_id: number
  id: number
  key1: string
  status: string
  updated_at: string
}

export interface AdditionalDocumentData {
  is_active: boolean
  items: AdditionalDocument[]
  total_items: number
}

export interface APIError {
  code: number
  error: string
  message: string
  status: number
}

export interface Earning {
  driver_pay: number
  end_date: string
  id: number
  license_number: string
  net_pay: number
  revenue: number
  start_date: string
  tax_amount: number
  tax_rate: number
  tip_amount: number
  tip_arrears: number
  total_completed_rides: number
}

export interface EarningDriverLetter {
  approved_at: string
  average: number
  can_request: boolean
  date: string
  date_birth: string
  driver: string
  first_trip: string
  is_available: boolean
  is_pending: boolean
  manager: string
  manager_email: string
  requested_at: string
}

export interface EarningRide {
  creation_date: string
  driver_earnings: number
  dropoff_completed_at: string
  dropoff_description: string
  dropoffDistance: number
  pickup_description: string
  pickup_distance: number
  ride_id: string
  status: string
  total_price: number
}

export interface EarningStats {
  earnings_last_8_weeks: {
    date: string
    earnings: number
  }[]
  rating: number
  total_earnings: number
  total_rides: number
  total_tips: number
}

export type Pagination = {
  page: number
  pageSize: number
}

export type SortParams = Record<string, string>

export type WeeklyReportApiResponse = {
  is_found: boolean
  url: string
}
